import {
	CashoutHubStore,
	OfferHubStore,
	ScorePredictorHubStore,
	UserHubStore,
} from "@state/hubs";
import ScorePredictorLiveStateStore from "@state/stores/score-predictor/ScorePredictorLiveStateStore";

import { httpClient } from "@utils";
import { action, computed, observable } from "mobx";

import { BetBuilderStore } from "@gp/slip";
import { BetBuilderService } from "@gp/hub";

import { IStoreLifeCycle } from "@interface";
import ResizeStore from "@state/shared/ResizeStore";
import MyBetsViewStore from "@state/stores/my-bets/MyBetsViewStore";
import { DiagnosticsProvider } from "@state/providers/diagnosticsProvider";
import { SportStatisticHubStore } from "@state/hubs/SportStatisticsHubStore";
import { SessionStorageProvider } from "@state/providers";

import { AutomaticLogoutStore } from "@state/stores/account-settings";

import BetSlipStore from "@state/bet-slip/BetSlipStore";
import { ChatViewStore } from "./chat/ChatViewStore";
import { ThemeStore } from "./components/subheader";
import { NotificationFavoritesStore } from "./notification-favorites/NotificationFavoritesStore";
import { BaseRootStore } from "./BaseRootStore";
import { BetBoosterHubStore } from "@state/hubs/BetBoosterHubStore";
import { XtremepushNotificationPermissionPopupStore } from "./crm";
import OnboardingWelcomePopupStore from "./popups/OnboardingWelcomePopupStore";
import BetBuilderPopupStore from "@state/bet-builder/BetBuilderPopupStore";
import GamePauseStore from "@state/shared/GamePauseStore";
import {
	RealityCheckStore,
	RequiredUserActionsStore,
	SessionExpireStore,
	User24hLockStore,
	UserAuthStore,
	WelcomeStore,
} from "@state/shared";
import { QuickPlayStore } from "@state/quick-play-store";
import CountryChangeStore from "@state/shared/CountryChangeStore";
import SearchStore from "./offer/search/SearchStore";
import { StorageStateKeysEnum } from "@utils/storage";
import { logger } from "@utils/logger/logger";
import CasinoSearchStore from "./games/CasinoSearchStore";
import GracePeriodStore from "@state/shared/GracePeriodStore";
import AreaChangeStore from "@state/shared/AreaChangeStore";
import WalletGracePeriodPopupStore from "@state/shared/WalletGracePeriodPopupStore";
import RetryHandlerStore from "@state/shared/RetryHandlerStore";
import WaitingPeriodStore from "@state/shared/WaitingPeriodStore";
import LogoutPopupStore from "@state/shared/LogoutPopupStore";
import { getApiRequestContext } from "@utils/api-config";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";

class RootStore extends BaseRootStore implements IStoreLifeCycle {
	platform: "web" = "web";

	@observable isAdditionalOfferVisible = false;
	@observable isSideMenuExpanded = true;

	myBetsViewStore: MyBetsViewStore;
	resizeStore: ResizeStore;
	gamePauseStore: GamePauseStore;

	constructor() {
		super();

		this.userAuthStore = new UserAuthStore(this);
		this.diagnosticsProvider = new DiagnosticsProvider(this.userAuthStore);

		this.themeStore = new ThemeStore(this);

		this.offerHub = new OfferHubStore(this.userAuthStore);
		this.betBoosterHubStore = new BetBoosterHubStore(this.userAuthStore);
		this.cashoutHubStore = new CashoutHubStore(this);
		this.userHubStore = new UserHubStore(this);
		this.scorePredictorLiveState = new ScorePredictorLiveStateStore();
		this.scorePredictorHub = new ScorePredictorHubStore(this);

		this.automaticLogoutStore = new AutomaticLogoutStore(this);
		this.realityCheckStore = new RealityCheckStore(this);
		this.sessionExpireStore = new SessionExpireStore(this);
		this.user24hLockStore = new User24hLockStore(this);
		this.onboardingWelcomePopupStore = new OnboardingWelcomePopupStore(
			this
		);
		this.userAuthStore.syncToken();
		// needs to happen because of possible wrong data formatting in acc key in local storage !!!!
		// can be removed after some time but doesn't hurt
		this.userAuthStore.checkBalanceEntry();

		this.welcomeStore = new WelcomeStore(this);
		this.requiredUserActionStore = new RequiredUserActionsStore(
			this.userAuthStore
		);

		this.quickPlayStore = new QuickPlayStore(this);

		this.chatViewStore = new ChatViewStore(this);
		this.gamePauseStore = new GamePauseStore(this);
		this.sportStatisticsHub = new SportStatisticHubStore(
			this.userAuthStore
		);

		this.notificationFavoritesStore = new NotificationFavoritesStore(
			this.userAuthStore
		);
		this.betBuilderPopupStore = new BetBuilderPopupStore(this);

		this.resizeStore = new ResizeStore();
		this.myBetsViewStore = new MyBetsViewStore(this);

		this.countryChangeStore = new CountryChangeStore(this);

		this.xtremepushNotificationPermissionPopupStore =
			new XtremepushNotificationPermissionPopupStore(this);

		this.searchStore = new SearchStore(this);
		this.casinoSearchStore = new CasinoSearchStore(this);

		SessionStorageProvider.set(
			StorageStateKeysEnum.PREMATCH_MENU_VERSION,
			"1"
		);

		this.gracePeriodStore = new GracePeriodStore(this);

		this.areaChangeStore = new AreaChangeStore(this);

		this.walletGracePeriodPopupStore = new WalletGracePeriodPopupStore(
			this
		);

		this.retryHandlerStore = new RetryHandlerStore(this);

		this.waitingPeriodStore = new WaitingPeriodStore(this);

		this.logoutPopupStore = new LogoutPopupStore(this);
	}

	@computed get calculatedOfferColumnsNumber() {
		return Math.min(
			this.isAdditionalOfferVisible
				? Math.floor(this.resizeStore.availableColumns / 2)
				: this.resizeStore.availableColumns,
			this.isAdditionalOfferVisible ? 2 : 4
		);
	}

	async onInitialize() {
		// need to fetch local storage values,
		// all other stores need to consume local storage after fetching
		await this.localStorageProvider.onInitialize();

		this.betSlipStore = new BetSlipStore({
			offerHub: this.offerHub.hub,
			userStore: this.userAuthStore,
			notificationStore: this.notificationStore,
			rootStore: this,
			balanceResolver: () => this.userAuthStore.resolveBalance(),
		});

		this.setLocalStorageLoadStatus(true);
		this.themeStore.setInitialValue();
		this.paymentOptionsStore.onStoreInitialize();
		this.userAuthStore.onInitialize();
		this.realityCheckStore.initializeRealityCheck();
		this.cashoutHubStore.initializeCashoutHub();
		this.userHubStore.initializeUserHub();
		this.sessionExpireStore.onInitialize();
		this.welcomeStore.onInitialize();

		this.onboardingWelcomePopupStore.onInitialize();

		this.betSlipStore.onInitialize();

		// used for requestId query param
		this.uniqueId = Date.now();
		this.chatViewStore.updateChatOnLoginChange(
			this.userAuthStore.isLoggedIn
		);

		this.checkIfIpRestricted();

		this.xtremepushNotificationPermissionPopupStore.onInitialize();
		this.betBuilderStore = new BetBuilderStore({
			isNextOddsEnabled: true,
			betSlipStore: this.betSlipStore.betSlipState,
			additionalOfferStores: [],
			betBuilderService: new BetBuilderService({
				apiClient: {
					get: async (relativeUrl: string) => {
						const context = getApiRequestContext();
						const appHeaders =
							getApplicationRequestHeaders(context);
						appHeaders["Content-Type"] = "application/json";
						const url = `${WEBPACK_BASE_PROXY_ADDRESS}${context?.agencyKey}/${relativeUrl}`;
						const response = await httpClient.get(url, appHeaders, {
							responseType: "raw",
						});
						return await response.json();
					},
				},
				callerEnvironment: WEBPACK_ENVIRONMENT_KEY,
			}),
			addRemoveErrorHandler: this.betSlipStore.addRemoveErrorHandler,
		});
		this.maintenanceStore.onInitialize();
		logger.setUserContext(this.userAuthStore);

		this.isStoreInitialized = true;
	}

	@action.bound
	resetPopups() {
		this.isSideMenuExpanded = false;
		this.isDepositLimitUpdatePopupActive = false;
	}

	@action.bound
	openSideMenu() {
		this.isSideMenuExpanded = true;
	}

	@action.bound
	closeSideMenu() {
		this.isSideMenuExpanded = false;
	}

	@action.bound
	closePopups() {
		this.realityCheckStore.deactivatePopup();
		this.automaticLogoutStore.deactivatePopup();
		this.accountVerificationStore.closePopup();
		this.welcomeStore.hidePopup();
	}

	@action.bound
	setAdditionalOfferVisible(value: boolean) {
		this.isAdditionalOfferVisible = value;
	}

	@action.bound
	toggleSideMenu() {
		if (this.isSideMenuExpanded) {
			this.closeSideMenu();
		} else {
			this.openSideMenu();
		}
	}

	@action.bound
	onDispose() {
		this.isStoreInitialized = false;
		this.paymentOptionsStore.onDispose();
		this.userAuthStore.onDispose();

		this.scorePredictorHub.onDispose?.();
		this.scorePredictorLiveState.onDispose();
	}
}

export default RootStore;
